// src/features/Planning/components/Step2Direction.jsx
import {
  Box,
  Flex,
  Radio,
  RadioGroup,
  Text,
  Button,
  Image,
  SimpleGrid,
} from "@chakra-ui/react";
import { FiSun } from "react-icons/fi";
import CompassImage from "../../../../assets/Planning/Compass/illustration-compass 1.png";
import RoadWithHuman from "../../../../assets/Planning/RoadImage/roadwithhuman.png";
import { normalizeDirection } from "../../../../utils/api";

const directionOptions = [
  { value: "north", label: "North", code: "NN", angle: 0 },
  { value: "north-east", label: "North East", code: "NE", angle: 45 },
  { value: "east", label: "East", code: "EE", angle: 90 },
  { value: "south-east", label: "South East", code: "SE", angle: 135 },
  { value: "south", label: "South", code: "SS", angle: 180 },
  { value: "south-west", label: "South West", code: "SW", angle: 225 },
  { value: "west", label: "West", code: "WW", angle: 270 },
  { value: "north-west", label: "North West", code: "NW", angle: 315 },
];

const angleMap = Object.fromEntries(
  directionOptions.map((d) => [d.value, d.angle])
);

export default function Step2PlotDirection({
  formData,
  setFormData,
  onNext,
  onSubmit,
  onBack,
  isLastStep,
  viewMode,
}) {
  const handleAction = isLastStep ? onSubmit : onNext;

  // FIX: formData.direction kabhi-kabhi non-canonical format mein aa sakti
  // hai (jaise "North_East", "North East", purani API se). normalizeDirection
  // se hamesha ek hi canonical lowercase-hyphenated value milti hai, taaki
  // radio highlight aur compass rotation har form ke liye consistently kaam kare.
  const normalizedDirection = normalizeDirection(formData.direction);

  const handleDirectionChange = (value) => {
    setFormData({ ...formData, direction: value });
  };

  const getCompassRotation = () => {
    if (!normalizedDirection) return 0;
    const selectedAngle = angleMap[normalizedDirection] ?? 0;
    return 180 - selectedAngle;
  };

  const compassRotation = getCompassRotation();

  const selectedOption = directionOptions.find(
    (d) => d.value === normalizedDirection
  );

  return (
    <Box
      h="600px" // ✅ ORIGINAL HEIGHT RESTORED
      w="100%"
      maxW="400px"
      mx="auto"
      display="flex"
      flexDirection="column"
      justifyContent="space-between"
      px={4}
      py={6} // original padding
    >
      <Box pointerEvents={viewMode ? "none" : "auto"}>
        {/* ✅ Heading */}
        <Text
          textAlign="center"
          fontSize="17px"
          fontWeight="600"
          color="gray.800"
          mb={2}
        >
          Select Your Plot Facing Direction
        </Text>

        {/* Compass */}
        <Flex justify="center" direction="column" align="center" mb={3}>

          <Box
            boxSize="165px" // slightly reduced (was 180)
            transform={`rotate(${compassRotation}deg)`}
            transition="transform 0.4s ease-in-out"
            display="flex"
            alignItems="center"
            justifyContent="center"
            position="relative"
          >
            <Image src={CompassImage} alt="Rotating Compass" boxSize="100%" objectFit="contain" />
            <Box
              position="absolute"
              top="50%"
              right="-10px"
              transform="translateY(-50%)"
              fontSize="24px"
            >
              <FiSun color="#FF8C00" />
            </Box>
          </Box>

          <Box mt={1} mb={1}>
            <Image src={RoadWithHuman} alt="Road" height="32px" objectFit="contain" />
          </Box>

          {selectedOption && (
            <Text
              fontSize="11px"
              fontWeight="600"
              color="cyan.600"
              mt={1}
              textAlign="center"
            >
              {selectedOption.label} ({selectedOption.code}) facing road
            </Text>
          )}
        </Flex>

        {/* Direction Grid */}
        <RadioGroup onChange={handleDirectionChange} value={normalizedDirection}>
          <SimpleGrid columns={2} spacing={2} maxW="330px" mx="auto">
            {directionOptions.map((dir) => (
              <Box
                key={dir.value}
                p={2}
                bg={normalizedDirection === dir.value ? "cyan.50" : "white"}
                border="1px solid"
                borderColor={normalizedDirection === dir.value ? "cyan.500" : "gray.200"}
                borderRadius="8px"
                transition="all 0.2s ease"
                cursor="pointer"
                onClick={() => handleDirectionChange(dir.value)}
                _hover={{ borderColor: "cyan.300", bg: "cyan.50" }}
              >
                <Radio value={dir.value} size="sm" colorScheme="cyan">
                  <Flex align="center" gap={1}>
                    <Text fontSize="12px" fontWeight="500" color="gray.700">
                      {dir.label}
                    </Text>
                    <Text fontSize="10px" color="cyan.500" fontWeight="600">
                      ({dir.code})
                    </Text>
                  </Flex>
                </Radio>
              </Box>
            ))}
          </SimpleGrid>
        </RadioGroup>
      </Box>

      {/* ✅ ORIGINAL BUTTONS (UNCHANGED) */}
      <Flex justify="space-between" gap={3}>
        <Button
          onClick={onBack}
          variant="solid"
          bg="white"
          color="cyan.600"
          border="1px solid"
          borderColor="cyan.500"
          borderRadius="6px"
          minW="100px"
          h="40px"
          fontWeight="500"
          fontSize="14px"
          _hover={{ bg: "cyan.50" }}
        >
          ← Previous
        </Button>

        <Button
          onClick={handleAction}
          isDisabled={viewMode ? false : !formData.direction}
          variant="solid"
          bg="cyan.500"
          color="white"
          borderRadius="6px"
          minW="100px"
          h="40px"
          fontWeight="500"
          fontSize="14px"
          ml="auto"
          _hover={{ bg: "cyan.600" }}
          _disabled={{ bg: "gray.300", color: "gray.500", cursor: "not-allowed" }}
        >
          {isLastStep ? "Submit" : "Next →"}
        </Button>
      </Flex>
    </Box>
  );
}